/* eslint-disable react-refresh/only-export-components */
import {
  createContext,
  useContext,
  useState,
  useEffect,
  useRef,
  type ReactNode,
  type DependencyList,
} from "react";

type Ctx = {
  headerActions: ReactNode;
  setHeaderActions: (node: ReactNode) => void;
};

const HeaderActionsContext = createContext<Ctx>({
  headerActions: null,
  setHeaderActions: () => {},
});

export function HeaderActionsProvider({ children }: { children: ReactNode }) {
  const [headerActions, setHeaderActions] = useState<ReactNode>(null);
  return (
    <HeaderActionsContext.Provider value={{ headerActions, setHeaderActions }}>
      {children}
    </HeaderActionsContext.Provider>
  );
}

export function useHeaderActionsSlot() {
  return useContext(HeaderActionsContext).headerActions;
}

/**
 * Register a header action button for the current page.
 * @param getNode  Factory that returns the ReactNode — called on mount and when deps change.
 *                 Stored in a ref so it always captures the latest closure without being a dep.
 * @param deps     Primitive values that should trigger a re-render of the slot (e.g. a boolean flag).
 *                 Keep this list minimal — only include values that change the visual output.
 */
export function useHeaderActions(
  getNode: () => ReactNode,
  deps: DependencyList = [],
) {
  const { setHeaderActions } = useContext(HeaderActionsContext);
  const factoryRef = useRef(getNode);
  // eslint-disable-next-line react-hooks/refs
  factoryRef.current = getNode; // always fresh, no stale closure

  useEffect(() => {
    setHeaderActions(factoryRef.current());
    return () => setHeaderActions(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps);
}
